import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, AlertTriangle, CheckCircle, Clock,
  ChevronRight, ChevronDown, RefreshCw, Download, X, FileText,
  Sparkles, User, Calendar, PenTool, Hash, ExternalLink,
  AlertCircle, ClipboardList, FileCheck, ShieldCheck, Timer,
  Target, Zap, GitBranch, BarChart3, Activity, Edit2, Save
} from 'lucide-react';

const BMS_DEPARTMENTS = {
  "0100": { name: "Sales and Estimating", color: "bg-blue-100 text-blue-800" },
  "0200": { name: "Project Administration", color: "bg-purple-100 text-purple-800" },
  "0300": { name: "Supply Chain", color: "bg-green-100 text-green-800" },
  "0400": { name: "Engineering", color: "bg-yellow-100 text-yellow-800" },
  "0500": { name: "Manufacturing", color: "bg-orange-100 text-orange-800" },
  "0600": { name: "Quality Control", color: "bg-red-100 text-red-700" },
  "0700": { name: "Aftermarket", color: "bg-teal-100 text-teal-800" },
  "0800": { name: "Accounts", color: "bg-indigo-100 text-indigo-800" },
  "0900": { name: "BMS Administration", color: "bg-pink-100 text-pink-800" },
  "1000": { name: "Human Resources", color: "bg-cyan-100 text-cyan-800" }
};

const CAPA_STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'investigation', label: 'Investigation', color: 'bg-blue-100 text-blue-700' },
  { value: 'action_planning', label: 'Action Planning', color: 'bg-purple-100 text-purple-700' },
  { value: 'implementation', label: 'Implementation', color: 'bg-orange-100 text-orange-700' },
  { value: 'verification', label: 'Verification', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'closed', label: 'Closed', color: 'bg-green-100 text-green-700' }
];

const CAPAManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [capas, setCapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCapa, setSelectedCapa] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [filters, setFilters] = useState({ status: '', department_code: '', severity: '' });
  const [searchQuery, setSearchQuery] = useState('');

  // Check if we're coming from ONCR with a specific CAPA ID
  const capaIdFromUrl = searchParams.get('id');
  const sourceFromUrl = searchParams.get('source');
  const sourceRefFromUrl = searchParams.get('source_ref');

  const fetchCapas = useCallback(async () => {
    try {
      let url = '/enhanced-capas';
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.department_code) params.append('department_code', filters.department_code);
      if (filters.severity) params.append('severity', filters.severity);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await apiClient.get(url);
      setCapas(response.data);
      
      // If coming from ONCR, auto-select that CAPA
      if (capaIdFromUrl) {
        const targetCapa = response.data.find(c => c.id === capaIdFromUrl);
        if (targetCapa) {
          setSelectedCapa(targetCapa);
        }
      }
    } catch (error) {
      console.error('Error fetching CAPAs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, capaIdFromUrl]);

  useEffect(() => {
    fetchCapas();
  }, [fetchCapas]);

  const filteredCapas = capas.filter(capa => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      capa.capa_number?.toLowerCase().includes(query) ||
      capa.title?.toLowerCase().includes(query) ||
      capa.job_number?.toLowerCase().includes(query) ||
      capa.source_reference?.toLowerCase().includes(query)
    );
  });

  const getDaysOpen = (createdAt) => {
    return differenceInDays(new Date(), new Date(createdAt));
  };

  const getAgingColor = (days, dueDate) => {
    const isOverdue = dueDate && new Date(dueDate) < new Date();
    if (isOverdue) return 'text-red-600 bg-red-50';
    if (days > 30) return 'text-orange-600 bg-orange-50';
    if (days > 14) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="capa-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">CAPA Management</h1>
          <p className="text-sm sm:text-base text-slate-500">Corrective & Preventive Actions</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          data-testid="new-capa-btn"
          className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          New CAPA
        </button>
      </div>

      {/* Source Info Banner (when coming from ONCR) */}
      {sourceFromUrl === 'oncr' && sourceRefFromUrl && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-purple-700">Linked from ONCR: {sourceRefFromUrl}</p>
              <p className="text-sm text-purple-500">Complete the root cause analysis and corrective actions below</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/oncrs')}
            className="flex items-center gap-2 px-3 py-1.5 text-purple-600 hover:bg-purple-100 rounded-lg text-sm"
          >
            <ExternalLink className="h-4 w-4" />
            Back to ONCR
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 shadow-sm space-y-3 sm:space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search CAPA #, title, job #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="capa-search"
          />
        </div>

        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <Filter className="h-4 w-4 text-slate-400 hidden sm:block" />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
          >
            <option value="">Status</option>
            {CAPA_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filters.department_code}
            onChange={(e) => setFilters({ ...filters, department_code: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
          >
            <option value="">Dept</option>
            {Object.entries(BMS_DEPARTMENTS).map(([code, info]) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
          <select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
          >
            <option value="">Severity</option>
            <option value="critical">Critical</option>
            <option value="major">Major</option>
            <option value="minor">Minor</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
        <div className="bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-slate-500 text-[10px] sm:text-sm">
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Total</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-slate-800 mt-0.5 sm:mt-1">{capas.length}</p>
        </div>
        <div className="bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-yellow-500 text-[10px] sm:text-sm">
            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Open</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-yellow-600 mt-0.5 sm:mt-1">
            {capas.filter(c => ['open', 'investigation', 'action_planning'].includes(c.status)).length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-blue-500 text-[10px] sm:text-sm">
            <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">In Progress</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-blue-600 mt-0.5 sm:mt-1">
            {capas.filter(c => ['implementation', 'verification'].includes(c.status)).length}
          </p>
        </div>
        <div className="hidden sm:block bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-red-500 text-[10px] sm:text-sm">
            <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Overdue</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-red-600 mt-0.5 sm:mt-1">
            {capas.filter(c => c.status !== 'closed' && c.due_date && new Date(c.due_date) < new Date()).length}
          </p>
        </div>
        <div className="hidden sm:block bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-green-500 text-[10px] sm:text-sm">
            <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Closed</span>
          </div>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {capas.filter(c => c.status === 'closed').length}
          </p>
        </div>
      </div>

      {/* CAPA List or Detail View */}
      {!selectedCapa ? (
        <div className="space-y-4">
          {filteredCapas.map((capa) => (
            <div
              key={capa.id}
              onClick={() => setSelectedCapa(capa)}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-300 cursor-pointer transition-all shadow-sm"
              data-testid={`capa-item-${capa.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="text-lg font-bold text-slate-800">{capa.capa_number}</span>
                    {capa.job_number && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">
                        <Hash className="h-3 w-3" />
                        Job: {capa.job_number}
                      </span>
                    )}
                    <span 
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        capa.severity === 'critical' ? 'bg-red-100' :
                        capa.severity === 'major' ? 'bg-orange-100' :
                        'bg-yellow-100'
                      }`}
                      style={{ 
                        color: capa.severity === 'critical' ? '#E00702' :
                               capa.severity === 'major' ? '#FB9703' : '#DFC03D'
                      }}
                    >
                      {capa.severity?.toUpperCase()}
                    </span>
                    {capa.source_reference && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                        Source: {capa.source_reference}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-slate-700 mb-1">{capa.title}</h3>
                  <p className="text-slate-600 text-sm line-clamp-2 mb-2">{capa.problem_statement}</p>
                  
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {capa.owner_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Due: {capa.due_date ? format(new Date(capa.due_date), 'MMM dd, yyyy') : 'N/A'}
                    </span>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${getAgingColor(getDaysOpen(capa.created_at), capa.due_date)}`}>
                      <Timer className="h-3 w-3" />
                      {getDaysOpen(capa.created_at)} days
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 ml-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    CAPA_STATUS_OPTIONS.find(s => s.value === capa.status)?.color || 'bg-gray-100'
                  }`}>
                    {capa.status?.replace('_', ' ').toUpperCase()}
                  </span>
                  {capa.five_why_analysis && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      RCA Done
                    </span>
                  )}
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            </div>
          ))}
          {filteredCapas.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No CAPAs found</p>
            </div>
          )}
        </div>
      ) : (
        <CAPADetailView 
          capa={selectedCapa}
          onBack={() => {
            setSelectedCapa(null);
            // Clear URL params when going back
            if (capaIdFromUrl) {
              navigate('/capa');
            }
          }}
          onUpdate={(updated) => {
            setSelectedCapa(updated);
            fetchCapas();
          }}
          user={user}
        />
      )}

      {/* New CAPA Modal */}
      {showNewModal && (
        <NewCAPAModal
          onClose={() => setShowNewModal(false)}
          onCreated={() => {
            fetchCapas();
            setShowNewModal(false);
          }}
          user={user}
        />
      )}
    </div>
  );
};

// CAPA Detail View with AI-Powered RCA
const CAPADetailView = ({ capa, onBack, onUpdate, user }) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    problem_details: true,
    root_cause_analysis: true,
    corrective_actions: false,
    verification: false
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [showFiveWhysModal, setShowFiveWhysModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localCapa, setLocalCapa] = useState(capa);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    setLocalCapa(capa);
    setEditForm({
      title: capa.title || '',
      description: capa.description || capa.problem_description || '',
      department: capa.department || '',
      priority: capa.priority || 'medium',
      due_date: capa.due_date ? capa.due_date.split('T')[0] : '',
      owner_name: capa.owner_name || ''
    });
  }, [capa]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getDaysOpen = (createdAt) => {
    return differenceInDays(new Date(), new Date(createdAt));
  };

  const generateAIAnalysis = async () => {
    setAnalyzing(true);
    try {
      // Use longer timeout for AI analysis (2 minutes)
      const response = await apiClient.post(`/enhanced-capas/${localCapa.id}/generate-analysis`, {}, {
        timeout: 120000
      });
      const updatedCapa = await apiClient.get(`/enhanced-capas/${localCapa.id}`);
      setLocalCapa(updatedCapa.data);
      onUpdate(updatedCapa.data);
      // Auto-open the 5 Whys modal to show results
      setShowFiveWhysModal(true);
    } catch (error) {
      console.error('Error generating analysis:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('AI analysis is taking longer than expected. Please try again in a moment.');
      } else {
        toast.error('Failed to generate AI analysis. Please try again.');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const updateCapa = async (updates) => {
    setSaving(true);
    try {
      await apiClient.patch(`/enhanced-capas/${localCapa.id}`, updates);
      const response = await apiClient.get(`/enhanced-capas/${localCapa.id}`);
      setLocalCapa(response.data);
      onUpdate(response.data);
      toast.success('CAPA updated successfully');
    } catch (error) {
      console.error('Error updating CAPA:', error);
      toast.error('Failed to update CAPA');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    await updateCapa(editForm);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditForm({
      title: localCapa.title || '',
      description: localCapa.description || localCapa.problem_description || '',
      department: localCapa.department || '',
      priority: localCapa.priority || 'medium',
      due_date: localCapa.due_date ? localCapa.due_date.split('T')[0] : '',
      owner_name: localCapa.owner_name || ''
    });
    setIsEditing(false);
  };

  const addCorrectiveAction = async (action) => {
    const actions = localCapa.corrective_actions || [];
    actions.push({
      id: `CA-${Date.now()}`,
      action: action.action,
      description: action.description,
      assigned_to: action.assigned_to,
      assigned_to_name: action.assigned_to_name,
      target_date: action.target_date,
      status: 'pending',
      created_at: new Date().toISOString()
    });
    
    await updateCapa({ 
      corrective_actions: actions,
      status: localCapa.status === 'open' || localCapa.status === 'investigation' ? 'action_planning' : localCapa.status
    });
  };

  const markActionComplete = async (actionIndex) => {
    const actions = [...(localCapa.corrective_actions || [])];
    if (actions[actionIndex]) {
      actions[actionIndex] = {
        ...actions[actionIndex],
        status: 'completed',
        completed_date: new Date().toISOString(),
        completed_by: user?.id,
        completed_by_name: user?.name
      };
      
      await updateCapa({ corrective_actions: actions });
      toast.success('Action marked as complete');
    }
  };

  const handleVerification = async (signatureData, signatureType) => {
    await updateCapa({
      verified_by: user?.id,
      verified_by_name: user?.name,
      verified_date: new Date().toISOString(),
      verification_signature: signatureData,
      verification_signature_type: signatureType,
      status: 'closed'
    });
    setShowSignatureModal(null);
  };

  const SectionHeader = ({ title, icon: Icon, section, badge, badgeColor = 'bg-blue-500' }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 transition-colors rounded-t-xl"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-blue-700" />
        <span className="font-semibold text-blue-800">{title}</span>
        {badge && (
          <span className={`px-2 py-0.5 ${badgeColor} text-white text-xs rounded-full`}>
            {badge}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronDown className="h-5 w-5 text-blue-600" />
      ) : (
        <ChevronRight className="h-5 w-5 text-blue-600" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
        data-testid="back-to-list"
      >
        <ChevronRight className="h-4 w-4 rotate-180" />
        Back to List
      </button>

      {/* Main Header Card */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {/* Header Bar */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold">{localCapa.capa_number}</h2>
              <p className="text-blue-100 text-sm mt-1">{localCapa.title}</p>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                localCapa.status === 'closed' ? 'bg-green-500/20 text-green-100' :
                'bg-white/20 text-white'
              }`}>
                {localCapa.status?.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Meta Row */}
          <div className="flex items-center gap-6 mt-4 text-sm text-blue-100 flex-wrap">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{localCapa.owner_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Due: {localCapa.due_date ? format(new Date(localCapa.due_date), 'MMM dd, yyyy') : 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              <span>{getDaysOpen(localCapa.created_at)} days open</span>
            </div>
            {localCapa.job_number && (
              <div className="flex items-center gap-2 bg-white/10 px-2 py-1 rounded">
                <Hash className="h-4 w-4" />
                <span className="font-medium">Job: {localCapa.job_number}</span>
              </div>
            )}
            {localCapa.source_reference && (
              localCapa.source_type === 'oncr' ? (
                <button
                  onClick={() => navigate(`/oncrs?highlight=${localCapa.source_id}`)}
                  className="flex items-center gap-2 bg-purple-500/30 px-2 py-1 rounded hover:bg-purple-500/50 transition-colors cursor-pointer"
                  data-testid="capa-oncr-link"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <span>Source: {localCapa.source_reference}</span>
                  <ExternalLink className="h-3 w-3" />
                </button>
              ) : (
                <div className="flex items-center gap-2 bg-purple-500/30 px-2 py-1 rounded">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Source: {localCapa.source_reference}</span>
                </div>
              )
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 p-4 bg-slate-50 border-b border-slate-200 flex-wrap">
          <button
            onClick={generateAIAnalysis}
            disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
            data-testid="generate-ai-btn"
          >
            {analyzing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Generate AI 5-Whys & Fishbone
          </button>
          {localCapa.five_why_analysis && (
            <button
              onClick={() => setShowFiveWhysModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 text-sm"
            >
              <GitBranch className="h-4 w-4" />
              View Root Cause Analysis
            </button>
          )}
          {localCapa.status !== 'closed' && localCapa.corrective_actions?.length > 0 && (
            <button
              onClick={() => setShowSignatureModal('verification')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <PenTool className="h-4 w-4" />
              Verify & Close CAPA
            </button>
          )}
          <button
            onClick={async () => {
              try {
                const response = await apiClient.get(`/enhanced-capas/${localCapa.id}/report/pdf`, {
                  responseType: 'blob'
                });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `CAPA_${localCapa.capa_number}_${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('PDF report downloaded successfully');
              } catch (error) {
                console.error('Error downloading PDF:', error);
                toast.error('Failed to download PDF report');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm ml-auto"
            data-testid="download-capa-pdf"
          >
            <Download className="h-4 w-4" />
            Download PDF Report
          </button>
        </div>

        {/* Collapsible Sections */}
        <div className="divide-y divide-slate-200">
          
          {/* Problem Details Section */}
          <div>
            <div className="flex items-center justify-between">
              <SectionHeader 
                title="PROBLEM DETAILS" 
                icon={Target} 
                section="problem_details" 
              />
              {expandedSections.problem_details && localCapa.status !== 'closed' && (
                <div className="pr-4">
                  {isEditing ? (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        <Save className="h-3 w-3" />
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-1 px-3 py-1 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-1 px-3 py-1 border border-blue-600 text-blue-600 rounded text-sm hover:bg-blue-50"
                      data-testid="edit-capa-btn"
                    >
                      <Edit2 className="h-3 w-3" />
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>
            {expandedSections.problem_details && (
              <div className="p-6 space-y-4">
                {isEditing ? (
                  /* Edit Mode */
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="text-xs text-slate-500 block mb-1">Title</label>
                        <input
                          type="text"
                          value={editForm.title}
                          onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Priority</label>
                        <select
                          value={editForm.priority}
                          onChange={(e) => setEditForm({...editForm, priority: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Problem Description</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        rows={3}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Department</label>
                        <select
                          value={editForm.department}
                          onChange={(e) => setEditForm({...editForm, department: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          {Object.entries(BMS_DEPARTMENTS).map(([code, dept]) => (
                            <option key={code} value={dept.name}>{code} - {dept.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Due Date</label>
                        <input
                          type="date"
                          value={editForm.due_date}
                          onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Owner</label>
                        <input
                          type="text"
                          value={editForm.owner_name}
                          onChange={(e) => setEditForm({...editForm, owner_name: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  /* View Mode */
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">CAPA Type</label>
                        <p className="font-medium capitalize">{localCapa.capa_type}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Severity</label>
                        <p 
                          className={`inline-block px-2 py-0.5 rounded text-sm font-medium ${
                            localCapa.severity === 'critical' ? 'bg-red-100' :
                            localCapa.severity === 'major' ? 'bg-orange-100' :
                            'bg-yellow-100'
                          }`}
                          style={{ 
                            color: localCapa.severity === 'critical' ? '#E00702' :
                                   localCapa.severity === 'major' ? '#FB9703' : '#DFC03D'
                          }}
                        >
                          {localCapa.severity?.charAt(0).toUpperCase() + localCapa.severity?.slice(1)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Department</label>
                        <p className="font-medium">{localCapa.department_code} - {localCapa.department}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Source</label>
                        {localCapa.source_type === 'oncr' && localCapa.source_id ? (
                          <button
                            onClick={() => navigate(`/oncrs?highlight=${localCapa.source_id}`)}
                            className="flex items-center gap-1 font-medium text-purple-600 hover:text-purple-800 hover:underline"
                            data-testid="capa-source-oncr-link"
                          >
                            <span>{localCapa.source_reference || 'ONCR'}</span>
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        ) : (
                          <p className="font-medium capitalize">{localCapa.source_type?.replace('_', ' ')}</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs text-slate-500">Problem Statement</label>
                      <p className="mt-1 text-slate-700 bg-slate-50 p-3 rounded-lg">{localCapa.problem_statement}</p>
                    </div>

                    {localCapa.iso_clause && (
                      <div>
                        <label className="text-xs text-slate-500">ISO Clause Reference</label>
                        <p className="font-medium">{localCapa.iso_clause}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Root Cause Analysis Section */}
          <div>
            <SectionHeader 
              title="ROOT CAUSE ANALYSIS" 
              icon={GitBranch} 
              section="root_cause_analysis"
              badge={localCapa.five_why_analysis ? '✓' : '!'}
              badgeColor={localCapa.five_why_analysis ? 'bg-green-500' : 'bg-orange-500'}
            />
            {expandedSections.root_cause_analysis && (
              <div className="p-6 space-y-4">
                {!localCapa.five_why_analysis && !localCapa.fishbone_analysis ? (
                  <div className="text-center py-8">
                    <Sparkles className="h-12 w-12 mx-auto mb-3 text-purple-400" />
                    <p className="text-slate-500 mb-4">No root cause analysis performed yet</p>
                    <button
                      onClick={generateAIAnalysis}
                      disabled={analyzing}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {analyzing ? 'Analyzing...' : 'Generate AI Analysis'}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Root Cause Summary */}
                    {localCapa.root_cause_summary && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <h4 className="font-semibold text-red-700 mb-2">Root Cause Identified</h4>
                        <p className="text-red-600">{localCapa.root_cause_summary}</p>
                      </div>
                    )}

                    {/* 5-Why Preview */}
                    {localCapa.five_why_analysis && !localCapa.five_why_analysis.parse_error && (
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-purple-800">AI 5-Why Analysis</h4>
                          <button
                            onClick={() => setShowFiveWhysModal(true)}
                            className="text-purple-600 hover:text-purple-700 text-sm flex items-center gap-1"
                          >
                            View Full Analysis <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          {localCapa.five_why_analysis.why_chain?.slice(0, 3).map((why, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-lg border-l-4 border-purple-400">
                              <p className="font-medium text-purple-700 text-sm">Why #{why.level}</p>
                              <p className="text-sm text-slate-800">{why.answer}</p>
                            </div>
                          ))}
                          {localCapa.five_why_analysis.why_chain?.length > 3 && (
                            <p className="text-sm text-purple-600 italic">+ {localCapa.five_why_analysis.why_chain.length - 3} more...</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Fishbone Preview */}
                    {localCapa.fishbone_analysis && !localCapa.fishbone_analysis.parse_error && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-800 mb-3">Fishbone Analysis (6M)</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {localCapa.fishbone_analysis.categories && Object.entries(localCapa.fishbone_analysis.categories).map(([key, category]) => (
                            <div key={key} className="bg-white p-3 rounded-lg text-sm">
                              <p className="font-medium text-blue-700">{category.category_name}</p>
                              <div className="text-xs text-slate-500 mb-1">
                                Relevance: {category.relevance_score}%
                              </div>
                              {category.causes?.slice(0, 2).map((cause, idx) => (
                                <p key={idx} className="text-xs text-slate-600">• {cause.cause}</p>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Corrective Actions Section */}
          <div>
            <SectionHeader 
              title="CORRECTIVE ACTIONS" 
              icon={Zap} 
              section="corrective_actions"
              badge={localCapa.corrective_actions?.filter(a => a.status === 'pending').length || null}
              badgeColor="bg-orange-500"
            />
            {expandedSections.corrective_actions && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-slate-500 mb-2 block">Corrective Action(s)</label>
                  <div className="space-y-3">
                    {(localCapa.corrective_actions || []).map((action, idx) => (
                      <div key={action.id || idx} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-slate-500">#{action.id?.split('-')[1] || idx + 1}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                action.status === 'completed' ? 'bg-green-100 text-green-700' :
                                action.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {action.status?.toUpperCase() || 'PENDING'}
                              </span>
                              {action.completed_date && (
                                <span className="text-xs text-green-600">
                                  Completed: {format(new Date(action.completed_date), 'MMM dd, yyyy')}
                                </span>
                              )}
                            </div>
                            <p className="font-medium text-slate-800">{action.description || action.action}</p>
                          </div>
                          <div className="text-right text-sm">
                            <div className="flex items-center gap-1 text-slate-500">
                              <User className="h-3 w-3" />
                              {action.assigned_to_name}
                            </div>
                            <div className="flex items-center gap-1 text-slate-500 mt-1">
                              <Calendar className="h-3 w-3" />
                              {action.target_date ? format(new Date(action.target_date), 'MMM dd, yyyy') : 'No date'}
                            </div>
                            {/* Complete Action Button */}
                            {action.status !== 'completed' && (
                              <button
                                onClick={() => markActionComplete(idx)}
                                className="mt-2 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
                              >
                                <CheckCircle className="h-3 w-3" />
                                Mark Complete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {localCapa.status !== 'closed' && (
                    <AddActionForm onAdd={addCorrectiveAction} type="corrective" />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Verification Section */}
          <div>
            <SectionHeader 
              title="VERIFICATION & CLOSURE" 
              icon={ShieldCheck} 
              section="verification"
              badge={localCapa.status === 'closed' ? '✓' : '!'}
              badgeColor={localCapa.status === 'closed' ? 'bg-green-500' : 'bg-red-500'}
            />
            {expandedSections.verification && (
              <div className="p-6 space-y-4">
                {localCapa.verified_by ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">CAPA Verified & Closed</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="text-xs text-slate-500">Verified by</label>
                        <p className="font-medium">{localCapa.verified_by_name}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Verified on</label>
                        <p className="font-medium">{format(new Date(localCapa.verified_date), 'MMM dd, yyyy HH:mm')}</p>
                      </div>
                    </div>
                    {localCapa.verification_signature && (
                      localCapa.verification_signature_type === 'drawn' ? (
                        <img 
                          src={localCapa.verification_signature} 
                          alt="Verification Signature" 
                          className="max-h-16 border border-slate-200 rounded bg-white p-2"
                        />
                      ) : (
                        <p className="font-signature text-xl italic">{localCapa.verification_signature}</p>
                      )
                    )}
                  </div>
                ) : (
                  <div className="p-4 border border-amber-300 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700 mb-3">
                      <AlertCircle className="h-5 w-5" />
                      <span>Verification pending - Complete corrective actions first</span>
                    </div>
                    {localCapa.corrective_actions?.length > 0 && (
                      <button
                        onClick={() => setShowSignatureModal('verification')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <PenTool className="h-4 w-4" />
                        Verify & Close CAPA
                      </button>
                    )}
                  </div>
                )}

                {localCapa.effectiveness_result && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-700 mb-2">Effectiveness Review</h4>
                    <p className="text-slate-600">{localCapa.effectiveness_result}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 5 Whys Modal */}
      {showFiveWhysModal && localCapa.five_why_analysis && (
        <FiveWhysModal
          analysis={localCapa.five_why_analysis}
          fishbone={localCapa.fishbone_analysis}
          onClose={() => setShowFiveWhysModal(false)}
        />
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          title="Verification Signature"
          onClose={() => setShowSignatureModal(null)}
          onSave={handleVerification}
        />
      )}
    </div>
  );
};

// 5 Whys Modal Component with AI Results
const FiveWhysModal = ({ analysis, fishbone, onClose }) => {
  const [activeTab, setActiveTab] = useState('5whys');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6" />
            <h3 className="text-xl font-bold">AI-Powered Root Cause Analysis</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('5whys')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === '5whys' 
                ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600' 
                : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <GitBranch className="h-4 w-4 inline mr-2" />
            5-Why Analysis
          </button>
          <button
            onClick={() => setActiveTab('fishbone')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'fishbone' 
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' 
                : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <BarChart3 className="h-4 w-4 inline mr-2" />
            Fishbone Diagram (6M)
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === '5whys' && analysis && (
            <div className="space-y-4">
              {/* Why Chain */}
              <div className="space-y-3">
                {analysis.why_chain?.map((why, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white border border-purple-200 rounded-lg p-4 shadow-sm"
                    style={{ marginLeft: `${idx * 20}px` }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">
                        {why.level}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-purple-600 font-medium mb-1">{why.question}</p>
                        <p className="text-slate-800">{why.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Root Cause Summary */}
              {analysis.root_cause && (
                <div className="mt-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-5 w-5 text-red-600" />
                    <h4 className="font-bold text-red-700">ROOT CAUSE IDENTIFIED</h4>
                  </div>
                  <p className="text-red-600 text-lg">{analysis.root_cause.summary}</p>
                  {analysis.root_cause.category && (
                    <p className="text-sm text-red-500 mt-2">Category: {analysis.root_cause.category}</p>
                  )}
                </div>
              )}

              {/* Recommended Actions */}
              {analysis.recommended_actions && analysis.recommended_actions.length > 0 && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-700 mb-2">Recommended Corrective Actions</h4>
                  <ul className="space-y-3">
                    {analysis.recommended_actions.map((action, idx) => (
                      <li key={idx} className="bg-white p-3 rounded-lg border border-green-200">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                          <div className="flex-1">
                            <p className="text-slate-800">{typeof action === 'string' ? action : action.action}</p>
                            {typeof action === 'object' && (
                              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                                {action.priority && (
                                  <span className={`px-2 py-0.5 rounded ${
                                    action.priority === 'High' ? 'bg-red-100 text-red-700' :
                                    action.priority === 'Medium' ? 'bg-orange-100 text-orange-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {action.priority} Priority
                                  </span>
                                )}
                                {action.type && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded capitalize">
                                    {action.type}
                                  </span>
                                )}
                                {action.responsible_party && (
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded">
                                    {action.responsible_party}
                                  </span>
                                )}
                                {action.target_completion && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                    {action.target_completion}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'fishbone' && fishbone && (
            <div className="space-y-4">
              {/* Problem Statement */}
              {fishbone.problem_statement && (
                <div className="p-4 bg-slate-100 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Problem Statement</p>
                  <p className="text-lg font-semibold text-slate-800">{fishbone.problem_statement}</p>
                </div>
              )}

              {/* 6M Categories */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {fishbone.categories && Object.entries(fishbone.categories).map(([key, category]) => (
                  <div 
                    key={key} 
                    className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-blue-700">{category.category_name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        category.relevance_score > 70 ? 'bg-red-100 text-red-700' :
                        category.relevance_score > 40 ? 'bg-orange-100 text-orange-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {category.relevance_score}%
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {category.causes?.map((cause, idx) => (
                        <li key={idx} className="text-sm">
                          <div className="flex items-start gap-2">
                            <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                              cause.likelihood === 'high' ? 'bg-red-500' :
                              cause.likelihood === 'medium' ? 'bg-orange-500' :
                              'bg-green-500'
                            }`} />
                            <span className="text-slate-700">{cause.cause}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Primary Cause */}
              {fishbone.primary_cause && (
                <div className="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
                  <h4 className="font-bold text-red-700 mb-2">PRIMARY CAUSE</h4>
                  <p className="text-red-600">{fishbone.primary_cause}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Action Form Component
const AddActionForm = ({ onAdd, type }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    action: '',
    description: '',
    assigned_to: '',
    assigned_to_name: '',
    target_date: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({ action: '', description: '', assigned_to: '', assigned_to_name: '', target_date: '' });
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="mt-3 flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm"
      >
        <Plus className="h-4 w-4" />
        Add {type === 'corrective' ? 'Corrective' : 'Preventive'} Action
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          placeholder="Action type"
          value={formData.action}
          onChange={(e) => setFormData({ ...formData, action: e.target.value })}
          required
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
        <input
          type="text"
          placeholder="Assigned to"
          value={formData.assigned_to_name}
          onChange={(e) => setFormData({ ...formData, assigned_to_name: e.target.value, assigned_to: e.target.value })}
          required
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
      </div>
      <textarea
        placeholder="Description of the action..."
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        required
        rows={2}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3"
      />
      <div className="flex items-center gap-3">
        <input
          type="date"
          value={formData.target_date}
          onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
          required
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          Add Action
        </button>
        <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Signature Modal Component
const SignatureModal = ({ title, onClose, onSave }) => {
  const [signatureType, setSignatureType] = useState('typed');
  const [typedSignature, setTypedSignature] = useState('');
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  React.useEffect(() => {
    if (signatureType === 'drawn' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [signatureType]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSave = () => {
    if (signatureType === 'typed') {
      if (!typedSignature.trim()) {
        alert('Please enter your signature');
        return;
      }
      onSave(typedSignature, 'typed');
    } else {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onSave(dataUrl, 'drawn');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSignatureType('typed')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              signatureType === 'typed' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}
          >
            Type Signature
          </button>
          <button
            onClick={() => setSignatureType('drawn')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              signatureType === 'drawn' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}
          >
            Draw Signature
          </button>
        </div>

        {signatureType === 'typed' ? (
          <div>
            <input
              type="text"
              placeholder="Type your full name"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-lg"
            />
            {typedSignature && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Preview:</p>
                <p className="font-signature text-2xl italic text-blue-800">{typedSignature}</p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              className="w-full border border-slate-300 rounded-lg cursor-crosshair"
            />
            <button onClick={clearCanvas} className="mt-2 text-sm text-blue-600 hover:text-blue-700">
              Clear
            </button>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={handleSave} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Save Signature
          </button>
        </div>
      </div>
    </div>
  );
};

// New CAPA Modal
const NewCAPAModal = ({ onClose, onCreated, user }) => {
  const [formData, setFormData] = useState({
    title: '',
    capa_type: 'corrective',
    source_type: 'internal',
    department: '',
    department_code: '',
    problem_statement: '',
    severity: 'major',
    owner_id: user?.id || '',
    owner_name: user?.name || '',
    due_date: '',
    job_number: '',
    iso_clause: ''
  });
  const [saving, setSaving] = useState(false);

  const handleDeptChange = (code) => {
    setFormData({
      ...formData,
      department_code: code,
      department: BMS_DEPARTMENTS[code]?.name || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/enhanced-capas', {
        ...formData,
        created_by: user?.id || 'current-user',
        created_by_name: user?.name || 'Current User'
      });
      onCreated();
    } catch (error) {
      console.error('Error creating CAPA:', error);
      alert('Failed to create CAPA');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">New CAPA</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              placeholder="Brief title for this CAPA"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">CAPA Type</label>
              <select
                value={formData.capa_type}
                onChange={(e) => setFormData({ ...formData, capa_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="corrective">Corrective</option>
                <option value="preventive">Preventive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select
                value={formData.source_type}
                onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="internal">Internal</option>
                <option value="oncr">ONCR</option>
                <option value="ncr">NCR</option>
                <option value="audit">Audit</option>
                <option value="customer_complaint">Customer Complaint</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
              <select
                required
                value={formData.department_code}
                onChange={(e) => handleDeptChange(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select Department</option>
                {Object.entries(BMS_DEPARTMENTS).map(([code, info]) => (
                  <option key={code} value={code}>{code} - {info.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="critical">Critical</option>
                <option value="major">Major</option>
                <option value="minor">Minor</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Job Number (Optional)</label>
              <input
                type="text"
                value={formData.job_number}
                onChange={(e) => setFormData({ ...formData, job_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., JOB-2026-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input
                type="date"
                required
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Problem Statement</label>
            <textarea
              required
              value={formData.problem_statement}
              onChange={(e) => setFormData({ ...formData, problem_statement: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              placeholder="Describe the problem in detail..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create CAPA'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CAPAManagement;
